import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Popover, Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { toast } from 'react-toastify';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

import logout from '@scale/frontend-lib/logout';
import { Icon } from '@scale/scaleui';
import { useUser } from 'react-scale/components/ui/UserContext';
import { CARD_ITEMS } from 'react-scale/corp/views/CorpProjectGroupManager/views/Home/HomeView';
import { ICON_MAP } from 'react-scale/corp/views/CorpProjectGroupManager/views/Home/HomeViewCard';
import { GenAIOpsHubPaths } from '../../../constants';
import { HeaderMenuCard } from '../../../views/Home/HeaderMenuCard';
import { MagicSearchButton } from 'react-scale/corp/views/CorpProjectGroupManager/modules/MagicSearch';
import { ReportIssueButton } from 'react-scale/corp/views/CorpProjectGroupManager/components/ReportIssue/ReportIssueButton';
import { OpsHubProfileMenu } from './OpsHubProfileMenu';
import { Button } from '@scale/scaleui-radix';
import NotificationBell from 'react-scale/corp/views/CorpProjectGroupManager/components/Notifications/NotificationBell';

interface LayoutHeaderProps {
  className?: string;
  noBorder?: boolean;
  leftSection?: React.ReactNode;
}

const LayoutHeader = ({ className, noBorder, leftSection }: LayoutHeaderProps) => {
  const user = useUser();

  const isQuestionBankEnabled = useUnleashFeatureFlag('question-bank-overview', {}, false);
  const gaiusProjectsCenterEnabled = useUnleashFeatureFlag(
    'gaius_projects_center',
    { userId: user.id },
    false,
  );
  const canSeeTnsCenter = useUnleashFeatureFlag('tns-center', { userId: user.id }, false);

  const filteredCardItems = useMemo(() => {
    const { isCorporateUser } = user;
    return CARD_ITEMS.filter(
      ({ nonCorpUsersAllowed, key }) =>
        (isCorporateUser || nonCorpUsersAllowed) &&
        (key !== 'question-bank' || isQuestionBankEnabled) &&
        (key !== 'gaius-projects-center' || gaiusProjectsCenterEnabled) &&
        (key !== 'trust-and-safety-center' || canSeeTnsCenter),
    ).map(({ key, shortTitle, to, icon }) => ({
      key,
      shortTitle,
      to,
      icon: ICON_MAP[icon],
    }));
  }, [user, isQuestionBankEnabled, gaiusProjectsCenterEnabled, canSeeTnsCenter]);

  return (
    <div
      className={twMerge(
        'flex justify-end w-full items-center py-2 px-5 bg-neutral-0 gap-2',
        !noBorder && 'border-b border-b-neutral-200',
        className,
      )}
    >
      <div className="flex items-center flex-wrap justify-between w-full gap-4 pl-3 overflow-x-hidden">
        <div className="flex flex-wrap items-center gap-4">{leftSection}</div>
        <div className="flex items-center gap-3 overflow-hidden">
          <MagicSearchButton className="w-48" />
          <div className="hidden md:block">
            <ReportIssueButton />
          </div>
        </div>
      </div>
      <NotificationBell />
      {filteredCardItems.length ? (
        <Popover className="relative">
          {({ open }) => (
            <>
              <Popover.Button
                className={twMerge(
                  'rounded-full hover:bg-neutral-200 focus:outline-none focus:bg-neutral-200 flex items-center justify-center',
                  open ? 'bg-neutral-200' : '',
                )}
              >
                <Icon icon="grid" size="lg" />
              </Popover.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel className="absolute mt-2 w-max px-4 -right-16 z-10">
                  <div className="overflow-hidden rounded-lg shadow-lg border border-neutral-200">
                    <div
                      className={`relative grid gap-2 bg-neutral-0 p-3 pb-0 grid-cols-${
                        filteredCardItems.length > 2 ? '3' : '2'
                      }`}
                    >
                      {filteredCardItems.map(({ key, shortTitle, to, icon }) => (
                        <HeaderMenuCard key={key} shortTitle={shortTitle} to={to} icon={icon} />
                      ))}
                    </div>
                    <div className="bg-neutral-0 p-3 !pt-0">
                      <Link to={GenAIOpsHubPaths.Main}>
                        <Button
                          color="accent"
                          variant="solid"
                          className="border border-neutral-200 hover:border hover:border-primary-300 w-full"
                        >
                          Return to GenAI Ops Hub
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      ) : null}
      <OpsHubProfileMenu
        firstName={user.firstName ?? ''}
        lastName={user.lastName ?? ''}
        email={user.email}
        userId={user.id}
        handleLogoutClick={() => {
          logout('/corp/login');
          toast.warning("You've logged out. We'll redirect you to login page now.", {
            autoClose: 5000,
          });
        }}
      />
    </div>
  );
};

export { LayoutHeader };
